import { AxiosError } from "axios";
import { MediaObject } from "shared-types";
import { checkIsYp } from "shared-auth";
import jobSettingsService from "shared/services/api/jobSettings/jobSettingsService";
import mediaService from "shared/services/api/media/mediaService";
import selfTapeRequestService from "shared/services/api/selfTapeRequest/selfTapeRequestService";

import { AsyncAction } from "shared/types/utilities";
import { ErrorCode } from "config/common";

import PageKey from "config/PageKey";
import {
  GetBreakdownPage,
  GetFollowUpPage,
  GetJobsPage,
  GetRolePage,
  GetSelfTapeRequestPage,
  GetSelfTapeUploadPage,
  SetPageData,
} from "store/page/page.types";

import { ActionType } from "store/types";
import breakdownService from "services/api/breakdown/breakdownService";
import followUpService from "services/api/followUp/followUpService";

function replaceNotFoundWithForbiddenError(e: AxiosError) {
  if (e.response && e.response.status === ErrorCode.NotFound) {
    e.response.status = ErrorCode.Forbidden;
  }

  throw e;
}

// ============================== Redux Actions =======================================================================

export const setPageData: SetPageData = (key, data) => ({
  type: ActionType.SetPageData,
  payload: {
    key,
    data,
  },
});

export const getJobsPage: AsyncAction<GetJobsPage> =
  (abortController) => async (dispatch) => {
    if (checkIsYp()) {
      dispatch(
        setPageData(PageKey.Jobs, {
          jobSettings: {
            performerSettings: null,
            agentSettings: null,
          },
        })
      );

      return;
    }

    const jobSettings = await jobSettingsService.get(abortController);

    dispatch(setPageData(PageKey.Jobs, { jobSettings }));
  };

export const getBreakdownPage: AsyncAction<GetBreakdownPage> =
  (breakdownId, abortController) => async (dispatch) => {
    try {
      const breakdown = await breakdownService.get(
        breakdownId,
        abortController
      );

      dispatch(setPageData(PageKey.Breakdown, { breakdown }));
    } catch (e) {
      replaceNotFoundWithForbiddenError(e);
    }
  };

export const getRolePage: AsyncAction<GetRolePage> =
  (breakdownId, roleId, abortController) => async (dispatch) => {
    try {
      const breakdown = await breakdownService.get(
        breakdownId,
        abortController
      );

      dispatch(
        setPageData(PageKey.Role, {
          breakdown: {
            ...breakdown,
            roles: breakdown.roles.filter(({ role }) => role.id === roleId),
          },
        })
      );
    } catch (e) {
      replaceNotFoundWithForbiddenError(e);
    }
  };

export const getFollowUpPage: AsyncAction<GetFollowUpPage> =
  (breakdownId, followUpId, abortController) => async (dispatch) => {
    try {
      const followUp = await followUpService.get(
        breakdownId,
        followUpId,
        abortController
      );

      dispatch(setPageData(PageKey.FollowUp, { followUp }));
    } catch (e) {
      replaceNotFoundWithForbiddenError(e);
    }
  };

export const getSelfTapeUploadPage: AsyncAction<GetSelfTapeUploadPage> =
  (performerRequestId, abortController) => async (dispatch) => {
    try {
      const selfTapeRequest =
        await selfTapeRequestService.getSelfTapeRequestForPerformerByPerformerRequestId(
          performerRequestId,
          abortController
        );

      dispatch(setPageData(PageKey.SelfTapeUpload, { selfTapeRequest }));
    } catch (e) {
      replaceNotFoundWithForbiddenError(e);
    }
  };

export const getSelfTapeRequestPage: AsyncAction<GetSelfTapeRequestPage> =
  (performerRequestId, abortController) => async (dispatch) => {
    try {
      const selfTapeRequest =
        await selfTapeRequestService.getSelfTapeRequestForPerformerByPerformerRequestId(
          performerRequestId,
          abortController
        );

      const mediaRequests = new Map<string, Promise<MediaObject>>();

      selfTapeRequest.submissionMedia.forEach(({ mediaInfoId, id }) =>
        mediaRequests.set(id, mediaService.get(mediaInfoId, abortController))
      );

      dispatch(
        setPageData(PageKey.SelfTapeRequest, { selfTapeRequest, mediaRequests })
      );
    } catch (e) {
      replaceNotFoundWithForbiddenError(e);
    }
  };
